import { DependencyList, EffectCallback, useEffect, useRef } from "react";

type Rc = {
  count: number;
  deps: DependencyList | undefined;
  cleanup: ReturnType<EffectCallback> | undefined;
};

const sameDeps = (a: DependencyList | undefined, b: DependencyList) =>
  a !== undefined &&
  a.length === b.length &&
  a.every((value, index) => Object.is(value, b[index]));

// A reference-counted effect: the setup runs when the first reference is
// taken, and the cleanup runs once the last reference is released.
//
// The release is deferred, so a reference taken right after it - as React
// StrictMode does by re-running the effects of a freshly mounted component -
// keeps the effect alive instead of cleaning it up and setting it up again.
const useRcEffect = (effect: EffectCallback, deps: DependencyList) => {
  const rcRef = useRef<Rc>({ count: 0, deps: undefined, cleanup: undefined });

  useEffect(() => {
    const rc = rcRef.current;

    if (rc.count === 0 && !sameDeps(rc.deps, deps)) {
      // The effect is pending the release, but the deps have changed, so
      // the effect has to be redone: clean up the old one right away.
      rc.cleanup?.();
      rc.cleanup = undefined;
      rc.deps = undefined;
    }

    if (rc.deps === undefined) {
      rc.cleanup = effect();
      rc.deps = deps;
    }

    rc.count += 1;

    return () => {
      rc.count -= 1;

      setTimeout(() => {
        if (rc.count !== 0 || rc.deps === undefined) return;

        rc.cleanup?.();
        rc.cleanup = undefined;
        rc.deps = undefined;
      }, 0);
    };
    // The deps are passed through from the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export default useRcEffect;
